import React, { useCallback, useMemo, useState, useRef } from "react";
import cx from "classnames";
import { Input } from "reactstrap";
import { debounce } from "lodash-es";
import { useTranslate } from "hooks";
import type { ChangeInputPayload } from "interfaces/shared.types";
import s from "components/ProductConfigurator/ProductConfigurator.module.scss";

type InputCardProps = {
  label: string;
  subLabel: boolean | string;
  value: number;
  displayValue: string;
  valueMin: number | null;
  valueMax: number | null;
  unit: string;
  isValueEditable: boolean;
  onChange: (payload: ChangeInputPayload | number) => void;
  testId: string;
  validationMessage: string;
  validationType: number;
  defaultValue: number;
};

export const InputCard = ({
  label,
  subLabel,
  value,
  displayValue,
  valueMin,
  valueMax,
  unit,
  isValueEditable,
  onChange,
  testId,
  validationMessage,
  validationType,
  defaultValue,
}: InputCardProps) => {
  const translate = useTranslate();
  const inputRef = useRef(null);
  const [currentValue, setCurrentValue] = useState<string | number>(value);

  const shouldValidate = useMemo(
    () => valueMin != null || valueMax != null,
    [valueMax, valueMin],
  );

  const numericValue = Number(currentValue);

  const isValid =
    !shouldValidate ||
    (!Number.isNaN(numericValue) &&
      (valueMin === null || numericValue >= valueMin) &&
      (valueMax === null || numericValue <= valueMax)) ||
    numericValue === Number(defaultValue);

  const debounceOnChange = useMemo(
    () =>
      debounce((val) => {
        onChange(val);
      }, 500),
    [onChange],
  );

  const handleChange = useCallback(
    ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      setCurrentValue(target.value);
      debounceOnChange(target.value);
    },
    [debounceOnChange],
  );

  const handleSend = useCallback(
    (e?: React.ChangeEvent<HTMLInputElement>) => {
      const sendValue = e?.target?.value ?? currentValue;
      if (sendValue === String(defaultValue)) {
        return;
      }
      if (sendValue === "" && defaultValue !== undefined) {
        setCurrentValue(String(defaultValue));
        return;
      }
      let numericValue = Number(sendValue);

      if (
        valueMin != null &&
        !isNaN(valueMin) &&
        (numericValue < valueMin || sendValue === "")
      ) {
        numericValue = valueMin;
      }
      if (valueMax != null && !isNaN(valueMax) && numericValue > valueMax) {
        numericValue = valueMax;
      }
      setCurrentValue(String(numericValue));
      if (numericValue !== value) {
        onChange(numericValue);
      }
    },
    [valueMin, valueMax, currentValue, value, onChange, defaultValue],
  );

  const handleInputKeyDown = useCallback(
    ({ key, keyCode }: { key: string; keyCode: number }) => {
      if (key === "Enter" || keyCode === 13) {
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <div className="position-relative">
      <div className={s.cardInputRow}>
        <div
          className={cx(
            { "pb-0": !isValid || validationMessage },
            s.cardInputRowResponsive,
          )}
        >
          <div className="d-flex flex-column align-items-start justify-content-center font-md ps-2">
            {label && <div dangerouslySetInnerHTML={{ __html: label }} />}
            {subLabel && (
              <div
                className="text-secondary font14"
                dangerouslySetInnerHTML={{ __html: subLabel }}
              />
            )}
          </div>
          <div
            className={cx(
              "d-flex align-items-start justify-content-center flex-column",
              s.inputWrapper,
            )}
          >
            <div className="d-flex align-items-center">
              {isValueEditable ? (
                <Input
                  type="number"
                  ref={inputRef}
                  value={currentValue}
                  invalid={!isValid}
                  onBlur={handleSend}
                  onChange={handleChange}
                  id={`input-feild-${testId}`}
                  className={cx(
                    "py-1 me-2 border border-gray-300 rounded-2 text-center",
                    { "border-danger": !isValid },
                    s.inputArea,
                  )}
                  data-testid={testId}
                  onKeyDown={handleInputKeyDown}
                />
              ) : (
                <span
                  className={cx(
                    "d-flex fs-md justify-content-center align-items-center bg-gray-100 me-2",
                    s.resultValue,
                  )}
                  data-testid={testId}
                >
                  {displayValue}
                </span>
              )}
              <div dangerouslySetInnerHTML={{ __html: unit }} />
            </div>
          </div>
        </div>
      </div>
      {isValueEditable && !isValid && (
        <div className="ps-2 mb-2 fs-sm text-danger">
          {translate("PRODUCT_VALUE_RANGE_WARNING", [valueMin, valueMax])}
        </div>
      )}
      {isValid && validationMessage && validationType !== 1 && (
        <div className="ps-2 mb-2 fs-sm text-warning">{validationMessage}</div>
      )}
    </div>
  );
};
